import { useCallback, useEffect, useState } from "react";
import { useWorkspaces } from "./useWorkspaces";
import { useEqms, type EqmsSection } from "./useEqms";
import type { WorkAreaType } from "../models/eqms";

// Single source of truth for the project-centric shell navigation. Owns useWorkspaces (workspace +
// project data) and useEqms (in-project section/work-area routing), plus the active target: either a
// shell view or a project-tree leaf. Shell views are reached from two places — the title bar's top
// menu (홈/앱/다운로드, platform- or workspace-wide) and the left rail (채팅/워크스페이스 관리 under the
// project tree; 내 초대/내 활동/내 계정/관리자 pinned at its bottom). The workspace is chosen at the top switcher; the left rail
// shows that workspace's projects as an expandable tree, which is the shell's main surface. The
// "workspace" shell view is workspace administration (members/invitations/projects/AI) for the
// workspace currently chosen in that switcher.

export type ShellView =
  "home" | "apps" | "downloads" | "chat" | "workspace" | "invitations" | "myActivity" | "account" | "admin";

/** A project-tree leaf maps to either a ProjectDetailView tab or an eQMS section (optionally scoped to a work area). */
export type ProjectLeaf =
  | { kind: "tab"; tab: "overview" | "backlog" | "blog" | "extapps" }
  | { kind: "eqms"; section: EqmsSection; area?: WorkAreaType };

export type Active =
  | { kind: "shell"; view: ShellView }
  | { kind: "project"; projectId: string; leaf: ProjectLeaf; label: string };

export function useProjectNav() {
  const ws = useWorkspaces();
  const eqms = useEqms();
  // 홈 is where the shell opens: the workspace's projects with their roll-up and the queues addressed
  // to this person. Landing in the app marketplace told a returning user nothing about their work.
  const [active, setActive] = useState<Active>({ kind: "shell", view: "home" });
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);

  // Pending invitations are addressed to the person, not the workspace — load them once so the
  // rail's 내 초대 badge is truthful before the user ever opens that screen.
  useEffect(() => {
    void ws.reloadMyInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.reloadMyInvitations]);

  // Auto-select the first workspace once the list loads (nothing chosen yet).
  useEffect(() => {
    if (!ws.workspace && ws.workspaces.length > 0) {
      ws.openWorkspace(ws.workspaces[0].id);
      eqms.selectWorkspace(ws.workspaces[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.workspaces, ws.workspace]);

  const selectWorkspace = useCallback((wid: string) => {
    ws.openWorkspace(wid);
    eqms.selectWorkspace(wid);
    setExpandedProjectId(null);
  }, [ws, eqms]);

  const showShell = useCallback((view: ShellView) => setActive({ kind: "shell", view }), []);

  const toggleProject = useCallback((pid: string) => {
    setExpandedProjectId((cur) => (cur === pid ? null : pid));
    if (ws.project?.id !== pid) ws.openProject(pid);
    if (eqms.projectId !== pid) eqms.selectProject(pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.project, eqms.projectId]);

  const openLeaf = useCallback((pid: string, leaf: ProjectLeaf, label: string) => {
    if (ws.project?.id !== pid) ws.openProject(pid);
    if (eqms.projectId !== pid) eqms.selectProject(pid);
    if (leaf.kind === "eqms") eqms.goTo(leaf.section, leaf.area ?? null);
    setActive({ kind: "project", projectId: pid, leaf, label });
    setExpandedProjectId(pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.project, eqms.projectId, eqms.goTo]);

  return { ws, eqms, active, expandedProjectId, selectWorkspace, showShell, toggleProject, openLeaf };
}

export type ProjectNavController = ReturnType<typeof useProjectNav>;
